import React, { useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import PngDownloadButton from './PngDownloadButton';
import { downloadSvgAsPng } from '../../../utils/svgExport';

// Metrik gezgininin JENERİK aylık trend grafiği (spec:
// docs/superpowers/specs/2026-08-03-ha-metrik-gezgini... — madde 1, kullanıcı onayı sohbette).
// HaTrendChart (dashboard) BİLEREK yeniden kullanılmadı: o bileşen yüzde-formatlı, delta-rozetli
// ve yön-yorumlu (lowerIsBetter) bir KPI kartıdır; gezgin ise HERHANGİ bir metriğin (sayı,
// yüzde, medyan yıl...) ham serisini nötr çizer — yön yorumu YAPMAZ, çünkü 45 metrik için
// "aşağı iyi mi?" tablosu tutmak yanlış yoruma açık bir bakım yüküdür.
//
// SVG desenleri SurvivalCurve ile aynı ailedendir: sayısal yükseklik (ResponsiveContainer'ın
// %100 tuzağı yok — burada zaten saf SVG), hairline ızgara, tabular-nums, ≥24px isabet alanı,
// PNG indirme (svgExport).
const DEFAULT_VIEW_WIDTH = 640;
const DEFAULT_HEIGHT = 280;
const MARGIN = { top: 16, right: 24, bottom: 40, left: 64 };
const HIT_AREA_RADIUS = 12;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

function linearScale(domainMin, domainMax, rangeMin, rangeMax) {
  const span = domainMax - domainMin;
  if (span <= 0) return () => (rangeMin + rangeMax) / 2;
  return (v) => rangeMin + ((v - domainMin) / span) * (rangeMax - rangeMin);
}

// hideDownload: kart sarmalayıcısının (HaShimmerCard) kendi PNG düğmesi varken ikinci düğme
// çizilmez. referenceValue/referenceLabel: yoğunlaşma metrikleri için "denge" yatay referans
// çizgisi (2026-08-03 kullanıcı geri bildirimi — değer, okuma anahtarı olmadan anlamsız
// kalıyordu). Bu bileşen eşiği HESAPLAMAZ, çağıranından alır: denge 1/k'dır (k = kullanımdaki
// kategori sayısı) ve metrikten metriğe değişir — cinsiyette %50, 9-Kutu'da %11. Sabit 0,5
// varsayımı ailenin yalnız iki metriğinde doğruydu (2026-09-03).
// viewWidth: SVG'nin SANAL genişliği. Dar kartlarda (Analiz Paneli ızgarası ~400px) varsayılan
// 640, ölçek ~0,6'ya düşürüp eksen yazılarını okunmaz yapıyordu (2026-08-03 kullanıcı geri
// bildirimi) — dar kart, kendi genişliğine yakın viewWidth geçer ki ölçek ~1 kalsın.
export default function MetricTrendChart({
  cells, formatValue, fileName, height = DEFAULT_HEIGHT, viewWidth = DEFAULT_VIEW_WIDTH,
  hideDownload = false, referenceValue = null, referenceLabel = null,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const svgRef = useRef(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { year: '2-digit', month: 'short' }),
    [i18n.language]
  );

  const points = useMemo(
    () => (cells || [])
      .filter((c) => c && c.value !== null && c.value !== undefined)
      .map((c) => ({ period: new Date(c.periodStart), value: Number(c.value), n: c.n })),
    [cells]
  );

  if (points.length < 2) {
    return <EmptyState>{t('hrAnalytics.metricDetail.notEnoughData')}</EmptyState>;
  }

  const innerWidth = viewWidth - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const values = points.map((p) => p.value);
  const rawMin = referenceValue !== null ? Math.min(...values, referenceValue) : Math.min(...values);
  const rawMax = referenceValue !== null ? Math.max(...values, referenceValue) : Math.max(...values);
  // Alan otomatik ama sıfır-tabanlı SIKIŞTIRMA yok: negatif değer (netChange) desteklenir;
  // düz seri (min==max) tek çizgiye çökmesin diye %5 nefes payı.
  const pad = (rawMax - rawMin) * 0.1 || Math.abs(rawMax) * 0.1 || 1;
  const yMin = rawMin - pad;
  const yMax = rawMax + pad;

  const xs = linearScale(0, points.length - 1, MARGIN.left, MARGIN.left + innerWidth);
  const ysc = linearScale(yMin, yMax, 0, innerHeight);
  const y = (v) => MARGIN.top + innerHeight - ysc(v);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xs(i)},${y(p.value)}`).join(' ');
  const areaPath = `${linePath} L${xs(points.length - 1)},${MARGIN.top + innerHeight} L${xs(0)},${MARGIN.top + innerHeight} Z`;

  // Etiket yoğunluğu SABİT SAYI değil, kullanılabilir genişlikten türetilir ("Oca 26" ~44px):
  // dar viewWidth'te etiketler kendiliğinden seyrekleşir, çakışmaz.
  const maxLabels = Math.max(3, Math.floor(innerWidth / 48));
  const labelStep = Math.ceil(points.length / maxLabels);
  const yTicks = [yMin, (yMin + yMax) / 2, yMax];
  const axisY = MARGIN.top + innerHeight;

  const handleDownload = () => {
    downloadSvgAsPng(svgRef.current, {
      fileName,
      background: theme.colors.surface.card,
    }).catch(() => {}); // indirme başarısızlığı sessizce yutulur — API hatası değil, toast kuralı dışı
  };

  return (
    <Wrapper>
      {!hideDownload && (
        <ToolbarRow>
          <PngDownloadButton dataRole="metric-download-png" onClick={handleDownload} />
        </ToolbarRow>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewWidth} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={t('hrAnalytics.metricDetail.trendAriaLabel')}
      >
        <defs>
          <linearGradient id="metric-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.colors.primary.main} stopOpacity={0.22} />
            <stop offset="100%" stopColor={theme.colors.secondary.main} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {yTicks.map((tv) => (
          <g key={`yt-${tv}`}>
            <line
              x1={MARGIN.left} y1={y(tv)}
              x2={MARGIN.left + innerWidth} y2={y(tv)}
              stroke={theme.colors.surface.border} strokeWidth={1}
            />
            <text
              x={MARGIN.left - 8} y={y(tv) + 3}
              textAnchor="end" fontSize={12}
              fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatValue(tv)}
            </text>
          </g>
        ))}

        {points.map((p, i) => (i % labelStep === 0 || i === points.length - 1) && (
          <text
            key={`xt-${i}`}
            x={xs(i)} y={axisY + 16}
            textAnchor="middle" fontSize={11}
            fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {monthFormatter.format(p.period)}
          </text>
        ))}

        {hoveredIdx !== null && points[hoveredIdx] && (
          <g data-role="metric-hover-guide" pointerEvents="none">
            <line
              x1={xs(hoveredIdx)} y1={MARGIN.top}
              x2={xs(hoveredIdx)} y2={axisY}
              stroke={theme.colors.text.muted} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.6}
            />
          </g>
        )}

        {referenceValue !== null && (
          <g data-role="metric-reference-line">
            <line
              x1={MARGIN.left} y1={y(referenceValue)}
              x2={MARGIN.left + innerWidth} y2={y(referenceValue)}
              stroke={theme.colors.text.muted} strokeWidth={1.5} strokeDasharray="5 4"
            />
            {referenceLabel && (
              <text
                x={MARGIN.left + innerWidth} y={y(referenceValue) - 5}
                textAnchor="end" fontSize={10}
                fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
              >
                {referenceLabel}
              </text>
            )}
          </g>
        )}

        <path d={areaPath} fill="url(#metric-trend-fill)" stroke="none" />
        <path data-role="metric-trend-path" d={linePath} fill="none" stroke={theme.colors.primary.main} strokeWidth={2} strokeLinejoin="round" />

        {points.map((p, i) => {
          const tooltip = `${monthFormatter.format(p.period)} — ${formatValue(p.value)}`;
          return (
            <g
              key={p.period.getTime()}
              data-role="metric-trend-point"
              tabIndex={0}
              role="img"
              aria-label={tooltip}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              onFocus={() => setHoveredIdx(i)}
              onBlur={() => setHoveredIdx(null)}
            >
              <title>{tooltip}</title>
              <circle cx={xs(i)} cy={y(p.value)} r={HIT_AREA_RADIUS} fill="transparent" />
              <circle cx={xs(i)} cy={y(p.value)} r={3.5} fill={theme.colors.primary.main} stroke={theme.colors.surface.card} strokeWidth={1.5} />
            </g>
          );
        })}
      </svg>
    </Wrapper>
  );
}
